import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useRequiredProfile } from '../lib/auth';
import { useOptionalWorkspace } from '../lib/workspace';
import { getAssessment, getRoadmap } from '../lib/api';
import { useWorkspaceQuery } from '../hooks/useWorkspaceQuery';
import { queryKeys } from '../lib/query-keys';
import type { RoadmapItem } from '@aigovops/core';
import { Alert, Badge, EmptyState, PageHeader, SeverityBadge, Spinner, StatusBadge } from '@aigovops/design-system';
import { daysUntil, fmtDate } from '../lib/format';

type RankAxis = 'deadline' | 'impact';

export function Roadmap() {
  const profileId = useRequiredProfile();
  const workspaceCtx = useOptionalWorkspace();
  const prefix = workspaceCtx ? `/w/${workspaceCtx.workspace.slug}/app` : '/app'; // scope-check-ignore: legacy fallback
  const roadmap = useWorkspaceQuery((s) => queryKeys.roadmap(s), () => getRoadmap(profileId, 20));
  const assess = useWorkspaceQuery((s) => queryKeys.capabilities(s, profileId), () => getAssessment(profileId));
  const frameworkNameMap = useMemo(
    () => new Map(assess.data?.framework_scores.map((fs) => [fs.framework_id, fs.framework_name]) ?? []),
    [assess.data]
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const rawFilter = searchParams.get('deadline');
  const deadlineFilter: DeadlineFilter = isDeadlineFilter(rawFilter) ? rawFilter : 'all';
  const setDeadlineFilter = (next: DeadlineFilter) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (next === 'all') params.delete('deadline');
        else params.set('deadline', next);
        return params;
      },
      { replace: true },
    );
  };

  const [rankAxis, setRankAxis] = useState<RankAxis>('deadline');
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const toggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (roadmap.loading) return <Spinner label="Loading roadmap" />;
  if (roadmap.error) return <Alert variant="error">{roadmap.error}</Alert>;
  if (!roadmap.data) return null;

  const now = new Date();
  const allItems = roadmap.data.items;
  const filteredItems = allItems.filter((it) =>
    matchesFilter(it, deadlineFilter, now),
  );

  const sortedItems = [...filteredItems].sort((a, b) => {
    switch (rankAxis) {
      case 'deadline': {
        const da = daysUntil(a.deadline, now);
        const db = daysUntil(b.deadline, now);
        if (da === null && db === null) return b.priority_weight - a.priority_weight;
        if (da === null) return 1;
        if (db === null) return -1;
        return da - db;
      }
      case 'impact':
        return b.priority_weight - a.priority_weight;
    }
  });

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
      <PageHeader
        title="Roadmap"
        subtitle={`Top ${allItems.length} of ${roadmap.data.total_gap_items} open items`}
        right={
          <SegmentedControl
            value={rankAxis}
            onChange={setRankAxis}
            options={[
              { value: 'deadline', label: 'By deadline' },
              { value: 'impact', label: 'By impact' },
            ]}
          />
        }
      />

      <RoadmapDeadlineStrip items={allItems} now={now} />

      <RoadmapFilterTabs
        items={allItems}
        now={now}
        active={deadlineFilter}
        onChange={setDeadlineFilter}
      />

      {sortedItems.length === 0 ? (
        deadlineFilter === 'all' ? (
          <EmptyState
            title="Nothing open on the roadmap"
            description="Every applicable item is done or not applicable."
          />
        ) : (
          <EmptyState
            title="No items match the deadline filter"
            description={`${allItems.length} items are on the roadmap; none fall into the "${DEADLINE_FILTER_LABEL[deadlineFilter]}" bucket.`}
          />
        )
      ) : (
        <ol style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 'var(--space-3)' }}>
          {sortedItems.map((r, idx) => {
            const days = daysUntil(r.deadline, now);
            const expanded = expandedIds.has(r.register_item_ref);
            return (
              <li
                key={r.register_item_ref}
                style={{
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-card)',
                  background: 'var(--surface)',
                  padding: 'var(--space-4)',
                  display: 'flex',
                  gap: 'var(--space-4)',
                  alignItems: 'flex-start',
                  fontFamily: 'var(--font-ui)',
                }}
              >
                <div
                  style={{
                    width: 'var(--space-8)',
                    textAlign: 'center',
                    paddingTop: '4px',
                    color: 'var(--text-dim)',
                    fontSize: 'var(--font-size-xl)',
                    fontWeight: 600,
                    fontFamily: 'var(--font-mono)',
                  }}
                >
                  {idx + 1}
                </div>
                <div style={{ flex: 1, display: 'grid', gap: 'var(--space-2)' }}>
                  <div style={{ display: 'flex', gap: 'var(--space-3)', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    <Link
                      to={`${prefix}/register/${r.register_item_ref}`}
                      style={{
                        fontSize: 'var(--font-size-md)',
                        fontWeight: 600,
                        color: 'var(--text)',
                        textDecoration: 'none',
                      }}
                    >
                      {r.title}
                    </Link>
                    <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexShrink: 0 }}>
                      <DeadlineChip days={days} deadline={r.deadline} />
                      <Badge>Effort {r.effort_estimate}</Badge>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexWrap: 'wrap' }}>
                    <SeverityBadge severity={r.severity} />
                    <StatusBadge status={r.status} />
                    {r.applicable_frameworks.slice(0, 3).map((f) => (
                      <Badge key={f}>{frameworkNameMap.get(f) ?? f}</Badge>
                    ))}
                    {r.applicable_frameworks.length > 3 ? (
                      <span style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-dim)' }}>
                        +{r.applicable_frameworks.length - 3}
                      </span>
                    ) : null}
                  </div>
                  <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center' }}>
                    <button
                      type="button"
                      onClick={() => toggleExpand(r.register_item_ref)}
                      aria-expanded={expanded}
                      style={{
                        appearance: 'none',
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        color: 'var(--accent)',
                        fontSize: 'var(--font-size-3xs)',
                        cursor: 'pointer',
                        fontWeight: 500,
                      }}
                    >
                      {expanded ? '▾ Hide details' : '▸ Details'}
                    </button>
                  </div>
                  {expanded ? (
                    <div style={{ borderTop: '1px solid var(--border)', paddingTop: 'var(--space-3)', display: 'grid', gap: 'var(--space-2)' }}>
                      <p style={{ margin: 0, fontSize: 'var(--font-size-2xs)', color: 'var(--text-muted)' }}>
                        {r.gap_description}
                      </p>
                      <p style={{ margin: 0, fontSize: 'var(--font-size-2xs)', color: 'var(--text-muted)' }}>
                        <strong style={{ color: 'var(--text)' }}>Recommended:</strong> {r.recommended_action}
                      </p>
                      {workspaceCtx ? (
                        <Link
                          to={`/w/${workspaceCtx.workspace.slug}/app/policies/new?category=${encodeURIComponent(r.category)}`}
                          style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--accent)', textDecoration: 'none', fontWeight: 500 }}
                        >
                          Draft a policy for this category →
                        </Link>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

// ---- Segmented rank-axis control ----------------------------------------

function SegmentedControl<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: string }[];
}) {
  return (
    <div
      role="radiogroup"
      aria-label="Sort axis"
      style={{
        display: 'inline-flex',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-input)',
        overflow: 'hidden',
      }}
    >
      {options.map((opt) => {
        const active = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(opt.value)}
            style={{
              appearance: 'none',
              padding: '6px 12px',
              border: 'none',
              borderRight: '1px solid var(--border)',
              background: active ? 'var(--accent)' : 'var(--surface)',
              color: active ? 'var(--text-inverse)' : 'var(--text-muted)',
              fontSize: 'var(--font-size-3xs)',
              fontWeight: active ? 700 : 500,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

// ---- Deadline chip (urgency-colored) ------------------------------------

function DeadlineChip({ days, deadline }: { days: number | null; deadline: string | null }) {
  if (days === null || !deadline) {
    return (
      <span style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-dim)' }}>No deadline</span>
    );
  }
  const overdue = days < 0;
  const urgent = days >= 0 && days < 14;
  const soon = days >= 14 && days < 30;
  const color = overdue || urgent ? 'var(--critical)' : soon ? 'var(--warning-fg)' : 'var(--text-dim)';
  const label = overdue ? `${-days}d overdue` : `${days}d`;
  return (
    <span
      style={{
        fontSize: 'var(--font-size-3xs)',
        color,
        fontWeight: overdue || urgent ? 600 : 500,
        fontFamily: 'var(--font-mono)',
      }}
      aria-label={overdue ? `Deadline overdue by ${-days} days` : `Deadline in ${days} days`}
    >
      {label} · {fmtDate(deadline)}
    </span>
  );
}

// ---- Deadline filter tabs (PR-Cluster-1-e) --------------------------

export type DeadlineFilter = 'all' | 'overdue' | 'lte_30d' | 'gt_30d_lte_90d' | 'no_deadline';

const DEADLINE_FILTER_VALUES: DeadlineFilter[] = [
  'all',
  'overdue',
  'lte_30d',
  'gt_30d_lte_90d',
  'no_deadline',
];

function isDeadlineFilter(input: string | null): input is DeadlineFilter {
  return input !== null && (DEADLINE_FILTER_VALUES as string[]).includes(input);
}

const DEADLINE_FILTER_LABEL: Record<DeadlineFilter, string> = {
  all: 'All',
  overdue: 'Overdue',
  lte_30d: '≤ 30d',
  gt_30d_lte_90d: '31–90d',
  no_deadline: 'No deadline',
};

const DEADLINE_FILTER_TONE: Record<DeadlineFilter, 'accent' | 'bad' | 'warn' | 'muted'> = {
  all: 'accent',
  overdue: 'bad',
  lte_30d: 'bad',
  gt_30d_lte_90d: 'warn',
  no_deadline: 'muted',
};

const FILTER_TONE_STYLE: Record<
  'accent' | 'bad' | 'warn' | 'muted',
  { activeBg: string; activeFg: string; activeBorder: string; idleBorder: string }
> = {
  accent: {
    activeBg: 'var(--accent)',
    activeFg: 'var(--text-inverse)',
    activeBorder: 'var(--accent)',
    idleBorder: 'var(--border)',
  },
  bad: {
    activeBg: 'var(--danger-bg)',
    activeFg: 'var(--danger-fg)',
    activeBorder: 'var(--band-red)',
    idleBorder: 'var(--border)',
  },
  warn: {
    activeBg: 'var(--warning-bg)',
    activeFg: 'var(--warning-fg)',
    activeBorder: 'var(--band-amber)',
    idleBorder: 'var(--border)',
  },
  muted: {
    activeBg: 'var(--surface-alt)',
    activeFg: 'var(--text-muted)',
    activeBorder: 'var(--border-strong)',
    idleBorder: 'var(--border)',
  },
};

function matchesFilter(item: RoadmapItem, filter: DeadlineFilter, now: Date): boolean {
  if (filter === 'all') return true;
  const days = daysUntil(item.deadline, now);
  if (filter === 'no_deadline') return days === null;
  if (days === null) return false;
  switch (filter) {
    case 'overdue':
      return days < 0;
    case 'lte_30d':
      return days >= 0 && days <= 30;
    case 'gt_30d_lte_90d':
      return days > 30 && days <= 90;
  }
}

function RoadmapFilterTabs({
  items,
  now,
  active,
  onChange,
}: {
  items: RoadmapItem[];
  now: Date;
  active: DeadlineFilter;
  onChange: (f: DeadlineFilter) => void;
}) {
  const counts = useMemo(() => {
    const result: Record<DeadlineFilter, number> = {
      all: items.length,
      overdue: 0,
      lte_30d: 0,
      gt_30d_lte_90d: 0,
      no_deadline: 0,
    };
    for (const it of items) {
      const days = daysUntil(it.deadline, now);
      if (days === null) {
        result.no_deadline++;
        continue;
      }
      if (days < 0) result.overdue++;
      else if (days <= 30) result.lte_30d++;
      else if (days <= 90) result.gt_30d_lte_90d++;
    }
    return result;
  }, [items, now]);

  const filters: DeadlineFilter[] = [
    'all',
    'overdue',
    'lte_30d',
    'gt_30d_lte_90d',
    'no_deadline',
  ];

  return (
    <div
      role="radiogroup"
      aria-label="Filter roadmap by deadline"
      style={{
        display: 'flex',
        gap: 'var(--space-2)',
        flexWrap: 'wrap',
      }}
    >
      {filters.map((f) => {
        const isActive = active === f;
        const count = counts[f];
        if (f !== 'all' && count === 0) return null;
        const tone = FILTER_TONE_STYLE[DEADLINE_FILTER_TONE[f]];
        return (
          <button
            key={f}
            type="button"
            role="radio"
            aria-checked={isActive}
            onClick={() => onChange(f)}
            style={{
              appearance: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 12px',
              borderRadius: '999px',
              border: `1px solid ${isActive ? tone.activeBorder : tone.idleBorder}`,
              background: isActive ? tone.activeBg : 'var(--surface)',
              color: isActive ? tone.activeFg : 'var(--text-muted)',
              fontSize: 'var(--font-size-3xs)',
              fontWeight: isActive ? 700 : 500,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            <span>{DEADLINE_FILTER_LABEL[f]}</span>
            <span
              style={{
                fontFamily: 'var(--font-mono)',
                fontVariantNumeric: 'tabular-nums',
                opacity: isActive ? 1 : 0.7,
              }}
            >
              {count}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ---- Deadline summary strip (PR-Cluster-1-d) ------------------------

function RoadmapDeadlineStrip({ items, now }: { items: RoadmapItem[]; now: Date }) {
  const buckets = useMemo(() => {
    let overdue = 0;
    let imminent = 0;
    let soon = 0;
    let nextItem: { item: RoadmapItem; days: number } | null = null;
    for (const it of items) {
      const days = daysUntil(it.deadline, now);
      if (days === null) continue;
      if (days < 0) overdue++;
      else if (days <= 30) imminent++;
      else if (days <= 90) soon++;
      if (days >= 0 && (nextItem === null || days < nextItem.days)) {
        nextItem = { item: it, days };
      }
    }
    return { overdue, imminent, soon, nextItem };
  }, [items, now]);

  const totalWithDeadline = buckets.overdue + buckets.imminent + buckets.soon;
  if (totalWithDeadline === 0 && buckets.nextItem === null) return null;

  return (
    <section
      aria-labelledby="roadmap-deadlines-heading"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-3)',
        padding: 'var(--space-3) var(--space-4)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-card)',
        background: 'var(--surface)',
        flexWrap: 'wrap',
      }}
    >
      <span
        id="roadmap-deadlines-heading"
        style={{
          fontSize: 'var(--font-size-3xs)',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: 'var(--text-dim)',
          fontWeight: 700,
          whiteSpace: 'nowrap',
        }}
      >
        Regulatory deadlines
      </span>
      <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
        {buckets.overdue > 0 ? (
          <DeadlinePill tone="bad" label="Overdue" value={buckets.overdue} />
        ) : null}
        {buckets.imminent > 0 ? (
          <DeadlinePill tone="bad" label="≤ 30d" value={buckets.imminent} />
        ) : null}
        {buckets.soon > 0 ? (
          <DeadlinePill tone="warn" label="31–90d" value={buckets.soon} />
        ) : null}
        {totalWithDeadline === 0 ? (
          <DeadlinePill tone="muted" label="> 90d" value={items.filter((i) => i.deadline).length} />
        ) : null}
      </div>
      {buckets.nextItem ? (
        <NextDeadlineChip item={buckets.nextItem.item} days={buckets.nextItem.days} />
      ) : null}
    </section>
  );
}

type DeadlineTone = 'bad' | 'warn' | 'muted';

const DEADLINE_TONE: Record<DeadlineTone, { bg: string; fg: string; border: string }> = {
  bad: {
    bg: 'var(--danger-bg)',
    fg: 'var(--danger-fg)',
    border: 'var(--band-red)',
  },
  warn: {
    bg: 'var(--warning-bg)',
    fg: 'var(--warning-fg)',
    border: 'var(--band-amber)',
  },
  muted: {
    bg: 'var(--surface-alt)',
    fg: 'var(--text-muted)',
    border: 'var(--border)',
  },
};

function DeadlinePill({ tone, label, value }: { tone: DeadlineTone; label: string; value: number }) {
  const t = DEADLINE_TONE[tone];
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'baseline',
        gap: '6px',
        padding: '4px 10px',
        borderRadius: '999px',
        background: t.bg,
        color: t.fg,
        border: `1px solid ${t.border}`,
        fontSize: 'var(--font-size-3xs)',
        fontWeight: 600,
        whiteSpace: 'nowrap',
      }}
    >
      <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </span>
      <span style={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {label}
      </span>
    </span>
  );
}

function NextDeadlineChip({ item, days }: { item: RoadmapItem; days: number }) {
  const tone: DeadlineTone = days <= 30 ? 'bad' : days <= 90 ? 'warn' : 'muted';
  const t = DEADLINE_TONE[tone];
  const countdown = days === 0 ? 'Due today' : `${days}d out`;
  return (
    <span
      style={{
        marginLeft: 'auto',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '4px 10px',
        borderRadius: 'var(--radius-input)',
        background: t.bg,
        color: t.fg,
        border: `1px solid ${t.border}`,
        fontSize: 'var(--font-size-3xs)',
        fontWeight: 500,
      }}
      title={`Deadline ${fmtDate(item.deadline!)} — ${countdown}`}
    >
      <span style={{ textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 700, opacity: 0.75 }}>
        Next
      </span>
      <span style={{ maxWidth: '18rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {item.title}
      </span>
      <span aria-hidden style={{ color: t.border, opacity: 0.6, fontFamily: 'var(--font-mono)' }}>·</span>
      <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700 }}>{countdown}</span>
    </span>
  );
}
